import React, { useEffect } from "react";
import {
  Sun,
  ArrowRight,
  CheckCircle2,
  Compass,
  ListChecks,
  CalendarClock,
  Link2,
  HeartHandshake,
  Quote,
  MapPin,
  MessageCircle,
} from "lucide-react";
import { PHOTOS, SPAIN_HERO, SPAIN_VILLAGE } from "@/lib/site-config";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import FloatingBookCTA from "@/components/site/FloatingBookCTA";
import usePageMeta from "@/lib/use-page-meta";

/* Redirect legacy NLV homepage anchors to /nlv-approved */
const useLegacyHashRedirect = () => {
  useEffect(() => {
    const h = window.location.hash;
    if (["#programme", "#video", "#book"].includes(h)) {
      window.location.replace(`/nlv-approved${h}`);
    }
  }, []);
};

/* ---------------- HERO ---------------- */
const Hero = () => (
  <section id="top" className="relative pt-28 pb-20 lg:pt-40 lg:pb-24 overflow-hidden editorial">
    <div className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />
    <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] rounded-full bg-[var(--gold)]/15 blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7">
        <div className="fade-up inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--cream-deep)] border border-[var(--terracotta)]/20 text-xs uppercase tracking-[0.2em] text-[var(--terracotta-deep)] font-medium" data-testid="home-hero-badge">
          <Sun className="w-3.5 h-3.5" />
          Personal, practical support for your move
        </div>

        <h1
          className="fade-up delay-100 font-serif text-[2.6rem] sm:text-[3.4rem] lg:text-[4.6rem] text-[var(--navy)] mt-6"
          data-testid="home-hero-headline"
        >
          Move to <em>Spain.</em>
          <br />
          <span className="underline-swoosh">Properly.</span>
        </h1>

        <p
          className="fade-up delay-200 mt-7 text-lg lg:text-xl text-[var(--ink-soft)] max-w-xl leading-relaxed"
          data-testid="home-hero-subhead"
        >
          Moving to Spain is exciting. Working out the right route, preparing the paperwork and
          doing everything in the right order can feel anything but. Get to Spain gives you clear,
          personal support for the move — whether you are applying through the Non-Lucrative Visa
          or moving with an EU passport.
        </p>

        <div className="fade-up delay-300 mt-10 flex flex-wrap gap-4" data-testid="home-hero-ctas">
          <a
            href="#programmes"
            className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
            data-testid="home-hero-cta-primary"
          >
            <Compass className="w-5 h-5" /> Find my route
          </a>
          <a
            href="#programmes"
            className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--navy)]/25 text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--cream)] font-medium"
            data-testid="home-hero-cta-secondary"
          >
            Explore the programmes <ArrowRight className="w-5 h-5" />
          </a>
        </div>

        <div className="fade-up delay-400 mt-12 flex items-center gap-6 text-sm text-[var(--muted)]">
          <div className="flex -space-x-3">
            {[PHOTOS.carley_solo, PHOTOS.carley_daughter, PHOTOS.carley_son].map((src, i) => (
              <img key={i} src={src} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-[var(--cream)]" />
            ))}
          </div>
          <div>
            <span className="block text-[var(--ink)] font-semibold">Hundreds of people guided · Personally supported by Carley Chaplin</span>
            <span className="block text-xs">Practical help from planning through to residency</span>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5 relative fade-in delay-300">
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
          <img src={SPAIN_HERO} alt="A sun-drenched Spanish village" className="absolute inset-0 w-full h-full object-cover slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--navy)]/55 via-transparent to-transparent" />
          <div className="absolute top-6 left-6 right-6 text-[var(--cream)]">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] mb-2 opacity-90">
              <MapPin className="w-3.5 h-3.5" /> Costa del Sol · 24°C
            </div>
            <p className="font-serif text-2xl leading-tight max-w-[70%]">"It's not a dream. It's a plan."</p>
          </div>
        </div>
        <div className="hidden lg:block absolute -bottom-12 -left-16 w-44 h-56 rounded-2xl overflow-hidden shadow-2xl rotate-[-6deg] border-[6px] border-[var(--cream)]">
          <img src={PHOTOS.carley_solo} alt="Carley in Spain" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- PROBLEM ---------------- */
const Problem = () => (
  <section className="relative py-24 lg:py-32 bg-[var(--navy)] text-[var(--cream)] overflow-hidden grain editorial">
    <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
      <img src={SPAIN_VILLAGE} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
      <div className="text-xs uppercase tracking-[0.35em] text-[var(--gold)] font-medium" data-testid="home-problem-eyebrow">
        The dream is simple. The process isn't always.
      </div>
      <h2 className="font-serif text-4xl lg:text-6xl mt-6 leading-[1.05]" data-testid="home-problem-headline">
        Spain is the dream.{" "}
        <em className="text-[var(--gold)]">Guesswork is the problem.</em>
      </h2>
      <p className="mt-10 text-[1.1rem] leading-relaxed text-[var(--cream)]/85 max-w-3xl">
        Most people do not struggle because there is no information. They struggle because there is
        too much of it — different answers from friends, forums, social media, professionals and
        people whose circumstances are nothing like their own. Get to Spain replaces that guesswork
        with a clear route, a sensible order and support built around your move.
      </p>
    </div>
  </section>
);

/* ---------------- PROGRAMME SELECTOR ---------------- */
const ProgrammeSelector = () => {
  const cards = [
    {
      id: "nlv",
      name: "NLV Approved",
      who: "For UK and other non-EU citizens planning to retire or live in Spain without working through the Non-Lucrative Visa.",
      bullets: [
        "Application planning and timeline",
        "Documents, translations and financial evidence",
        "Forms and submission preparation",
        "Personal support through the process",
      ],
      href: "/nlv-approved",
      cta: "Explore NLV Approved",
    },
    {
      id: "eu",
      name: "EU Approved",
      who: "For people moving to Spain where they or their spouse or partner hold an EU, EEA or Swiss passport.",
      bullets: [
        "Personal relocation plan",
        "Documents, healthcare and residency preparation",
        "Appointment readiness and sequencing",
        "Support for individual and mixed-nationality circumstances",
      ],
      href: "/eu-approved",
      cta: "Explore EU Approved",
    },
  ];

  return (
    <section id="programmes" className="py-24 lg:py-32 relative editorial">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">Choose your route</div>
          <h2 className="font-serif text-4xl lg:text-6xl text-[var(--navy)] mt-5 leading-[1.02]" data-testid="home-programmes-headline">
            Two programmes.<br />
            <em>One properly planned move.</em>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-14">
          {cards.map((c) => (
            <div
              key={c.id}
              className="rounded-[2rem] p-8 lg:p-10 bg-white border border-black/5 shadow-sm hover:shadow-xl transition-shadow duration-500 flex flex-col"
              data-testid={`programme-card-${c.id}`}
            >
              <div className="inline-flex self-start items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--cream-deep)] text-xs uppercase tracking-[0.2em] text-[var(--terracotta-deep)] font-medium">
                <Sun className="w-3.5 h-3.5" /> {c.name}
              </div>
              <h3 className="font-serif text-3xl text-[var(--navy)] mt-5">{c.name}</h3>
              <p className="mt-3 text-[var(--ink-soft)] leading-relaxed">{c.who}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {c.bullets.map((b, i) => (
                  <li key={i} className="flex gap-3 text-[var(--ink-soft)]">
                    <CheckCircle2 className="w-5 h-5 text-[var(--terracotta)] flex-shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
              <a
                href={c.href}
                className="pill mt-8 inline-flex self-start items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
                data-testid={`programme-card-${c.id}-cta`}
              >
                {c.cta} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-[2rem] p-8 lg:p-10 bg-[var(--cream-deep)] border border-black/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="home-route-help">
          <div>
            <div className="font-serif text-2xl text-[var(--navy)]">Not sure which route applies?</div>
            <p className="mt-2 text-[var(--ink-soft)]">
              Message Carley on Facebook — tell her a little about your passport situation and
              plans, and she'll point you towards the most useful next step.
            </p>
          </div>
          <a
            href="https://m.me/gettospain"
            target="_blank"
            rel="noreferrer"
            className="pill inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--navy)] text-[var(--cream)] font-medium flex-shrink-0"
            data-testid="home-route-help-cta"
          >
            <MessageCircle className="w-4 h-4" /> Message Carley
          </a>
        </div>
      </div>
    </section>
  );
};

/* ---------------- DIFFERENTIATORS ---------------- */
const Differentiators = () => {
  const items = [
    {
      icon: Compass,
      title: "Built around your circumstances",
      body: "Your family, finances, work, healthcare, accommodation and timeline affect what your move looks like. Your plan should reflect that.",
    },
    {
      icon: ListChecks,
      title: "The right things in the right order",
      body: "One early decision can affect several later steps. Get to Spain helps you understand what needs to happen, when and what comes next.",
    },
    {
      icon: CalendarClock,
      title: "Preparation before appointments",
      body: "Know which forms, payments and supporting documents you are likely to need before you attend.",
    },
    {
      icon: Link2,
      title: "Trusted specialist connections",
      body: "When regulated or specialist help is appropriate, avoid starting another blind search. Get to Spain can point you towards relevant trusted providers where available.",
    },
    {
      icon: HeartHandshake,
      title: "Personal access to Carley",
      body: "Practical support from someone who understands the process and the reality of building a life in Spain.",
    },
  ];

  return (
    <section id="how" className="py-24 lg:py-32 bg-[var(--cream-deep)]/60 relative editorial">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
            A different kind of relocation support
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] mt-5 leading-[1.05]" data-testid="home-diff-headline">
            Clear answers. A proper plan.<br />
            <em>A real person beside you.</em>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mt-14">
          {items.map((it, i) => (
            <div key={i} className="soft-card rounded-2xl p-7 hover:bg-white transition-colors duration-500 group" data-testid={`home-diff-item-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[var(--terracotta)]/10 text-[var(--terracotta)] flex items-center justify-center mb-5 group-hover:bg-[var(--terracotta)] group-hover:text-[var(--cream)] transition-colors duration-500">
                <it.icon className="w-5 h-5" strokeWidth={2} />
              </div>
              <h3 className="font-serif text-2xl text-[var(--navy)] leading-tight">{it.title}</h3>
              <p className="mt-3 text-[var(--ink-soft)] text-[0.95rem] leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- FOUR-STAGE FRAMEWORK ---------------- */
const Framework = () => {
  const stages = [
    { n: "01", title: "Plan your move", body: "Establish the likely route, timeline, family circumstances and important dependencies." },
    { n: "02", title: "Prepare before leaving", body: "Organise the relevant documents, translations, finances, healthcare and appointments." },
    { n: "03", title: "Move to Spain", body: "Arrive knowing what needs doing first and what evidence to take." },
    { n: "04", title: "Settle successfully", body: "Complete the relevant residency and practical registrations, with specialist help where needed." },
  ];
  return (
    <section className="py-24 lg:py-32 bg-[var(--navy)] text-[var(--cream)] relative overflow-hidden editorial grain">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <h2 className="font-serif text-4xl lg:text-6xl leading-[1.02] max-w-3xl" data-testid="home-framework-headline">
          From <em className="text-[var(--gold)]">"we'd love to"</em> to living in Spain.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          {stages.map((s, i) => (
            <div key={i} className="rounded-2xl p-7 bg-[var(--navy-deep)]/60 border border-[var(--cream)]/10" data-testid={`home-stage-${i}`}>
              <div className="font-serif text-4xl text-[var(--gold)]/70">{s.n}</div>
              <h3 className="font-serif text-2xl mt-4 leading-tight">{s.title}</h3>
              <p className="mt-3 text-[var(--cream)]/80 text-[0.95rem] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- ABOUT CARLEY ---------------- */
const About = () => (
  <section id="about" className="py-24 lg:py-32 relative overflow-hidden editorial">
    <div className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full bg-[var(--gold)]/10 blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5 relative">
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-xl">
          <img src={PHOTOS.family_yacht} alt="Carley with her family in Spain" className="w-full h-full object-cover" />
        </div>
        <div className="absolute -bottom-10 -right-6 lg:-right-12 w-44 h-56 rounded-2xl overflow-hidden shadow-xl border-4 border-[var(--cream)] rotate-[5deg]">
          <img src={PHOTOS.family_kids_selfie} alt="The Chaplin family in Spain" className="w-full h-full object-cover" />
        </div>
        <span className="tape -top-3 left-12" />
      </div>

      <div className="lg:col-span-7">
        <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">Meet your guide</div>
        <h2 className="font-serif text-4xl lg:text-6xl text-[var(--navy)] mt-5 leading-[1.02]" data-testid="home-about-headline">
          Hi, I'm <em>Carley.</em>
        </h2>
        <div className="mt-6 space-y-5 text-[var(--ink-soft)] text-[1.05rem] leading-relaxed">
          <p>
            I created <strong className="text-[var(--navy)]">Get to Spain</strong> after seeing how many people were
            trying to make one of the biggest changes of their lives using scattered information,
            conflicting advice and crossed fingers.
          </p>
          <p>
            My role is to make the process calmer and clearer. I help you understand the route,
            prepare properly and know what to do next — without turning your move into another
            full-time job.
          </p>
          <p>
            I've personally worked with <strong className="text-[var(--navy)]">hundreds of people</strong> developing
            their plans for life in Spain, and I bring that practical experience to every programme.
            In 2023, we made the move ourselves — relocating our entire family and everything we
            owned from the UK to Spain.
          </p>
        </div>
        <a
          href="/about"
          className="mt-8 inline-flex items-center gap-2 text-sm text-[var(--terracotta)] font-medium underline underline-offset-4 hover:text-[var(--terracotta-deep)]"
          data-testid="home-about-more-link"
        >
          Read our family's story <ArrowRight className="w-4 h-4" />
        </a>
        <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
          <Stat n="100s" l="People guided" />
          <Stat n="2" l="Programmes" />
          <Stat n="1:1" l="Personal access" />
        </div>
      </div>
    </div>
  </section>
);

const Stat = ({ n, l }) => (
  <div className="border-l-2 border-[var(--terracotta)] pl-4">
    <div className="font-serif text-3xl text-[var(--navy)]">{n}</div>
    <div className="text-xs uppercase tracking-wider text-[var(--muted)] mt-1">{l}</div>
  </div>
);

/* ---------------- PROOF ---------------- */
const Proof = () => (
  <section id="stories" className="py-24 lg:py-32 bg-[var(--navy)] text-[var(--cream)] relative overflow-hidden editorial grain">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-[0.3em] text-[var(--gold)] font-medium">Real stories</div>
        <h2 className="font-serif text-4xl lg:text-6xl mt-5 leading-[1.02]" data-testid="home-proof-headline">
          From <em className="text-[var(--gold)]">"someday"</em> to a properly planned move.
        </h2>
      </div>

      <div className="mt-16 grid lg:grid-cols-5 gap-6 rounded-[2rem] overflow-hidden bg-[var(--navy-deep)]/60 border border-[var(--gold)]/30" data-testid="home-testimonial-featured">
        <div className="lg:col-span-2 relative min-h-[340px] lg:min-h-0">
          <img src={PHOTOS.carley_clients} alt="Carley with clients Glen and partner" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[var(--navy)]/60 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--cream)]">
            <span className="w-2 h-2 rounded-full bg-[var(--gold)]" />
            Met with Carley in September
          </div>
        </div>
        <figure className="lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center">
          <Quote className="w-9 h-9 text-[var(--gold)]" />
          <blockquote className="mt-5 font-serif text-xl lg:text-2xl leading-[1.4] text-[var(--cream)]">
            "THANK YOU for helping us!!! If you're considering meeting with anyone to discuss
            moving to Spain, I highly recommend Carley. She is awesome! We met with her in
            September and she <em className="text-[var(--gold)] not-italic font-medium">knows her stuff</em> — well organised and informed! We are well
            on our way to life in Spain thanks to her. She is an excellent relocation specialist!"
          </blockquote>
          <figcaption className="mt-8 pt-6 border-t border-[var(--cream)]/15">
            <div className="font-medium text-lg">Glen</div>
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--gold)] mt-1">@happydayglen · Moving to Spain</div>
          </figcaption>
        </figure>
      </div>

      <div className="mt-8 text-center">
        <a href="/client-success" className="inline-flex items-center gap-2 text-sm text-[var(--gold)] underline underline-offset-4 hover:text-[var(--cream)] transition-colors" data-testid="home-more-stories-link">
          Read all client success stories <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
);

/* ---------------- FREE TOOLS ---------------- */
const FreeTools = () => (
  <section className="py-24 lg:py-28 bg-[var(--cream-deep)]/60 editorial">
    <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
      <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">Start where you are</div>
      <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] mt-5 leading-[1.05]" data-testid="home-tools-headline">
        Not ready for personal support yet?<br />
        <em>Start with something useful.</em>
      </h2>
      <p className="mt-6 text-[var(--ink-soft)] text-lg max-w-2xl mx-auto">
        Use the free Get to Spain tools to understand your route, assess your readiness and begin
        planning with more confidence.
      </p>
      <a
        href="/tools"
        className="pill mt-9 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--navy)] text-[var(--cream)] font-medium"
        data-testid="home-tools-cta"
      >
        Explore the free tools <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  </section>
);

/* ---------------- FINAL CTA ---------------- */
const FinalCTA = () => (
  <section className="py-24 lg:py-32 relative overflow-hidden editorial">
    <div className="absolute -top-20 -left-32 w-[500px] h-[500px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />
    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
      <h2 className="font-serif text-4xl lg:text-6xl text-[var(--navy)] leading-[1.02]" data-testid="home-final-headline">
        Ready to work out <em>your next step?</em>
      </h2>
      <p className="mt-6 text-[var(--ink-soft)] text-lg max-w-2xl mx-auto">
        Tell us a little about your passport situation and plans. We will point you towards the
        programme, call or resource that makes the most sense right now.
      </p>
      <a
        href="#programmes"
        className="pill mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
        data-testid="home-final-cta"
      >
        Find my next step <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  </section>
);

/* ---------------- PAGE ---------------- */
export default function Home() {
  useLegacyHashRedirect();
  usePageMeta(
    "Get to Spain | Personal Support for Moving to Spain",
    "Clear, personal support for moving to Spain through the Non-Lucrative Visa or with an EU passport. Find the right route and plan your next step."
  );
  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)] selection:bg-[var(--terracotta)] selection:text-[var(--cream)]" data-testid="home-page">
      <SiteNav />
      <Hero />
      <Problem />
      <ProgrammeSelector />
      <Differentiators />
      <Framework />
      <About />
      <Proof />
      <FreeTools />
      <FinalCTA />
      <SiteFooter />
      <FloatingBookCTA href="#programmes" label="Find my route" />
    </main>
  );
}
